import { debounce } from 'throttle-debounce';
import renderMinicart from '../lib/renderMinicart';
import getCart from '../lib/getCart';
import updateCartCount from '../lib/updateCartCount';
import { SELECTORS, HIDDEN_CLASS, SWATCHES } from '../utils/constants';

const POINTER_EVENTS_NONE = 'pointer-events-none';
const LOADING_OPACITY = 'opacity-60';
const ARIA_PRESSED = 'aria-pressed';
const COUNT_PLACEHOLDER = '{{ count }}';
const FOCUS_DEBOUNCE_MS = 300;


const selectedVariants = new Set();
let existingSwatchCount = 0;

const fetchExistingSwatchCount = async () => {
  try {
    const cart = await getCart();
    if (!cart) return 0;
    return cart.items
      .filter((item) => item.properties && item.properties[SWATCHES.PROPERTY_KEY] === 'true')
      .reduce((sum, item) => sum + item.quantity, 0);
  } catch (error) {
    console.warn('Could not fetch cart for swatch count:', error);
    return 0;
  }
};

const updateUI = () => {
  const addBtn = document.querySelector('[data-swatch-add-to-cart]');
  const btnLabel = document.querySelector('[data-swatch-btn-label]');
  const stickyBar = document.querySelector('[data-swatch-sticky-bar]');
  const stickyCount = document.querySelector('[data-swatch-sticky-count]');
  const limitMsg = document.querySelector('[data-swatch-library-limit-message]');
  const cards = document.querySelectorAll('[data-swatch-card]');

  const count = selectedVariants.size;
  const totalSelected = count + existingSwatchCount;
  const remaining = SWATCHES.MAX_QUANTITY - totalSelected;
  const atCartLimit = existingSwatchCount >= SWATCHES.MAX_QUANTITY;

  stickyBar?.classList.toggle(HIDDEN_CLASS, count === 0);

  if (stickyCount) {
    const available = SWATCHES.MAX_QUANTITY - existingSwatchCount;
    const selectedText = window.theme.strings.swatchSelectedCount
      .replace(COUNT_PLACEHOLDER, count)
      .replace('{{ available }}', available);
    stickyCount.textContent = count > 0 ? selectedText : '';
  }

  cards.forEach((card) => {
    const isSelected = selectedVariants.has(card.dataset.variantId);
    if (isSelected) {
      card.dataset.selected = 'true';
      card.setAttribute(ARIA_PRESSED, 'true');
    } else {
      delete card.dataset.selected;
      card.setAttribute(ARIA_PRESSED, 'false');
    }
    card.disabled = atCartLimit || (!isSelected && remaining <= 0);
  });

  if (addBtn) {
    addBtn.disabled = count === 0 || atCartLimit;

    if (btnLabel) {
      if (count === 0) {
        btnLabel.textContent = addBtn.dataset.defaultLabel ?? '';
      } else if (count === 1) {
        btnLabel.textContent = addBtn.dataset.singleLabel ?? '';
      } else {
        btnLabel.textContent = (addBtn.dataset.pluralLabel ?? '').replace('{count}', String(count));
      }
    }
  }

  if (limitMsg) {
    const { strings } = window.theme;
    if (atCartLimit) {
      limitMsg.textContent = strings.swatchAtLimit.replace('{{ max }}', SWATCHES.MAX_QUANTITY);
    } else if (existingSwatchCount > 0 && count === 0) {
      const template = existingSwatchCount === 1 ? strings.swatchInBasketOne : strings.swatchInBasketOther;
      limitMsg.textContent = template.replace(COUNT_PLACEHOLDER, existingSwatchCount).replace('{{ remaining }}', remaining);
    } else if (remaining <= 0) {
      limitMsg.textContent = strings.swatchMaxReached.replace('{{ max }}', SWATCHES.MAX_QUANTITY);
    } else {
      limitMsg.textContent = '';
    }
  }
};

const initTabs = (signal) => {
  const filterBar = document.querySelector('[data-swatch-filter-bar]');
  if (!filterBar) return;

  const showAllBtn = filterBar.querySelector('[data-swatch-show-all]');
  const tabs = [...filterBar.querySelectorAll('[data-swatch-tab]')];
  const allButtons = [showAllBtn, ...tabs].filter(Boolean);

  const showAll = () => {
    if (showAllBtn) {
      showAllBtn.setAttribute(ARIA_PRESSED, 'true');
      showAllBtn.tabIndex = 0;
    }
    tabs.forEach((t) => {
      t.setAttribute(ARIA_PRESSED, 'false');
      t.tabIndex = -1;
    });
    document.querySelectorAll('[data-swatch-panel]').forEach((panel) => {
      panel.classList.remove(HIDDEN_CLASS);
    });
  };

  const selectTab = (tab) => {
    if (showAllBtn) {
      showAllBtn.setAttribute(ARIA_PRESSED, 'false');
      showAllBtn.tabIndex = -1;
    }
    const { swatchTab } = tab.dataset;
    tabs.forEach((t) => {
      t.setAttribute(ARIA_PRESSED, t === tab ? 'true' : 'false');
      t.tabIndex = t === tab ? 0 : -1;
    });
    document.querySelectorAll('[data-swatch-panel]').forEach((panel) => {
      panel.classList.toggle(HIDDEN_CLASS, panel.dataset.swatchPanel !== swatchTab);
    });
  };

  showAll();

  filterBar.addEventListener('click', (event) => {
    if (event.target.closest('[data-swatch-show-all]')) {
      showAll();
    } else {
      const tab = event.target.closest('[data-swatch-tab]');
      if (tab) selectTab(tab);
    }
  }, { signal });

  filterBar.addEventListener('keydown', (event) => {
    const { key } = event;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
    const currentIndex = allButtons.findIndex((b) => b === document.activeElement);
    if (currentIndex === -1) return;
    event.preventDefault();
    let nextIndex;
    if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % allButtons.length;
    else if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + allButtons.length) % allButtons.length;
    else if (key === 'Home') nextIndex = 0;
    else nextIndex = allButtons.length - 1;
    allButtons[nextIndex].focus();
    if (allButtons[nextIndex] === showAllBtn) showAll();
    else selectTab(allButtons[nextIndex]);
  }, { signal });
};

const initSwatchLibrary = async () => {
  selectedVariants.clear();
  existingSwatchCount = 0;

  const controller = new AbortController();
  const { signal } = controller;

  const sectionEl = document.querySelector('[data-swatch-library]');
  const sectionId = sectionEl?.dataset.sectionId;
  document.addEventListener(
    'shopify:section:unload',
    (e) => { if (e.detail.sectionId === sectionId) controller.abort(); },
    { signal },
  );

  initTabs(signal);

  existingSwatchCount = await fetchExistingSwatchCount();
  updateUI();

  const syncCart = async () => {
    existingSwatchCount = await fetchExistingSwatchCount();
    updateUI();
  };

  window.addEventListener('focus', debounce(FOCUS_DEBOUNCE_MS, syncCart), { signal });

  const minicartContent = document.querySelector('[data-minicart-content]');
  if (minicartContent) {
    const observer = new MutationObserver(syncCart);
    observer.observe(minicartContent, { childList: true });
    signal.addEventListener('abort', () => observer.disconnect());
  }

  document.addEventListener('click', async (event) => {
    const card = event.target.closest('[data-swatch-card]');
    if (card) {
      const { variantId } = card.dataset;
      if (!variantId) return;

      if (selectedVariants.has(variantId)) {
        selectedVariants.delete(variantId);
        delete card.dataset.selected;
        card.setAttribute(ARIA_PRESSED, 'false');
      } else {
        if (selectedVariants.size + existingSwatchCount >= SWATCHES.MAX_QUANTITY) return;
        selectedVariants.add(variantId);
        card.dataset.selected = 'true';
        card.setAttribute(ARIA_PRESSED, 'true');
      }

      updateUI();
      return;
    }

    const addBtn = event.target.closest('[data-swatch-add-to-cart]');
    if (!addBtn || selectedVariants.size === 0) return;

    const spinner = addBtn.querySelector(SELECTORS.LOADING_SPINNER);
    const btnLabel = addBtn.querySelector('[data-swatch-btn-label]');

    addBtn.disabled = true;
    addBtn.classList.add(POINTER_EVENTS_NONE, LOADING_OPACITY);
    spinner?.classList.remove(HIDDEN_CLASS);

    try {
      const latestCount = await fetchExistingSwatchCount();
      if (latestCount + selectedVariants.size > SWATCHES.MAX_QUANTITY) {
        existingSwatchCount = latestCount;
        addBtn.classList.remove(POINTER_EVENTS_NONE, LOADING_OPACITY);
        spinner?.classList.add(HIDDEN_CLASS);
        updateUI();
        return;
      }

      const count = selectedVariants.size;
      const items = Array.from(selectedVariants).map((id) => ({
        id: parseInt(id, 10),
        quantity: 1,
        properties: { [SWATCHES.PROPERTY_KEY]: 'true' },
      }));

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Theme-ATC': '1' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || `Cart add failed: ${response.status}`);
      }

      const [cart] = await Promise.all([getCart(), renderMinicart()]);
      updateCartCount(cart.item_count);

      spinner?.classList.add(HIDDEN_CLASS);
      addBtn.classList.remove(POINTER_EVENTS_NONE, LOADING_OPACITY);

      if (btnLabel) {
        const { strings } = window.theme;
        btnLabel.textContent = count === 1
          ? strings.swatchAddedOne
          : strings.swatchAddedOther.replace(COUNT_PLACEHOLDER, count);
      }

      existingSwatchCount = latestCount + count;
      selectedVariants.clear();
      updateUI();
      // Match the regular add-to-cart flow: open the minicart once it has re-rendered.
      window.theme.minicart?.open();
    } catch (error) {
      console.error('Error adding swatches to cart:', error);
      addBtn.classList.remove(POINTER_EVENTS_NONE, LOADING_OPACITY);
      spinner?.classList.add(HIDDEN_CLASS);
      updateUI();
    }
  }, { signal });
};

export default initSwatchLibrary;
